import { ref } from 'vue'
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  uploadString,
  listAll,
  type StorageReference,
  type UploadMetadata,
  type ListResult,
  type StorageError,
  type FullMetadata,
  getMetadata
} from 'firebase/storage'
import { useFirebaseApp } from 'vuefire'

export interface UploadOptions {
  path: string;
  metadata?: UploadMetadata;
  generateUniqueName?: boolean;
}

// 스토리지 파일 정보 인터페이스 정의
export interface StorageFileInfo {
  name: string;
  path: string;
  ref: StorageReference;
  uploadedAt: Date | null;
  uploadedBy: string | undefined;
  contentType: string | undefined;
  size: number | undefined;
  // 필요에 따라 size, contentType 등의 메타데이터 추가 가능
}

export const useStorage = () => {
  const storage = getStorage(useFirebaseApp())
  const loading = ref(false)
  const progress = ref(0)
  const error = ref<StorageError | null>(null)
  const downloadURL = ref<string | null>(null)

  // --- 파일 목록 관련 상태 ---
  const listLoading = ref(false)
  const listError = ref<StorageError | null>(null)
  const files = ref<StorageFileInfo[]>([])
  // --- 파일 목록 관련 상태 끝 ---

  /**
   * 지정된 경로의 파일 목록 가져오기
   */
  const listFiles = async (pathPrefix: string): Promise<StorageFileInfo[]> => {
    listLoading.value = true
    listError.value = null
    files.value = []

    try {
      const listRef = storageRef(storage, pathPrefix)
      const res: ListResult = await listAll(listRef)

      // 각 파일의 메타데이터를 병렬로 가져오기
      const metadataPromises = res.items.map(itemRef => getMetadata(itemRef));
      // Promise.allSettled 사용: 일부 메타데이터 로드 실패해도 전체가 실패하지 않음
      const metadataResults = await Promise.allSettled(metadataPromises);

      const fileList: StorageFileInfo[] = [];
      metadataResults.forEach((result, index) => {
        const itemRef = res.items[index]; // 현재 아이템 참조 가져오기
        if (result.status === 'fulfilled') {
          // 성공 시 메타데이터 추출
          const metadata: FullMetadata = result.value;
          fileList.push({
            name: metadata.name,
            path: metadata.fullPath,
            ref: itemRef,
            size: metadata.size,
            uploadedAt: metadata.timeCreated ? new Date(metadata.timeCreated) : null,
            // customMetadata에서 uploadedBy 가져오기 (없으면 undefined)
            uploadedBy: metadata.customMetadata?.uploadedBy,
            contentType: metadata.contentType
          });
        } else {
          // 메타데이터 가져오기 실패 시 처리
          console.error(`Failed to get metadata for ${itemRef.fullPath}:`, result.reason);
          // 실패한 파일 정보라도 기본 정보만 포함하여 목록에 추가할 수 있음 (선택적)
          fileList.push({
            name: itemRef.name,
            path: itemRef.fullPath,
            ref: itemRef,
            size: 0, // 알 수 없음
            uploadedAt: null, // 알 수 없음
            uploadedBy: undefined,
            contentType: undefined // 알 수 없음
          });
        }
      });

      // 이름순 또는 시간순 정렬 (옵션)
      fileList.sort((a, b) => (b.uploadedAt?.getTime() || 0) - (a.uploadedAt?.getTime() || 0)); // 최신순 정렬

      files.value = fileList
      return fileList

    } catch (err: any) {
      // listAll 자체가 실패하는 경우 (예: 경로 없음, 권한 없음)
      // storage/object-not-found 에러는 여기서 잡힐 수 있음
      if (err.code === 'storage/object-not-found') {
        console.log(`Path not found or empty: ${pathPrefix}`);
        // 이 경우는 오류라기보다는 빈 상태일 수 있으므로 listError 설정 안 함
      } else {
        listError.value = err as StorageError
        console.error(`Error listing files for '${pathPrefix}':`, err)
      }
      files.value = [] // 오류 시 목록 비우기
      return [];
    } finally {
      listLoading.value = false
    }
  }

  /**
   * 파일 객체 업로드
   */
  const uploadFile = async (file: File, options: UploadOptions): Promise<string | null> => {
    if (!file) return null

    loading.value = true
    error.value = null
    progress.value = 0
    downloadURL.value = null

    try {
      let filePath = options.path
      // generateUniqueName 옵션이 true이면 파일 경로에 타임스탬프 추가
      if (options.generateUniqueName && !filePath.endsWith(file.name)) {
        // 경로 끝에 파일 이름이 명시적으로 없는 경우 (예: 'profile-images/userId/')
        const timestamp = Date.now();
        const extension = file.name.split('.').pop();
        filePath = `${options.path}/${timestamp}.${extension}`;
      } else if (options.generateUniqueName && filePath.endsWith(file.name)) {
        // 경로 끝에 파일 이름이 이미 있는 경우 (예: 'knowledge-base/userId/filename.txt')
        // 파일명 앞에 타임스탬프 추가 또는 다른 고유화 로직 적용 가능
        const dir = filePath.substring(0, filePath.lastIndexOf('/'));
        const filename = filePath.substring(filePath.lastIndexOf('/') + 1);
        const timestamp = Date.now();
        const extension = filename.split('.').pop();
        const baseName = filename.substring(0, filename.lastIndexOf('.'));
        filePath = `${dir}/${baseName}_${timestamp}.${extension}`;
        console.warn(`generateUniqueName=true 이지만 경로에 파일명이 포함되어 있어 파일명을 ${filePath} 로 변경합니다.`);
      }
      // 기본 경로는 options.path 사용

      const fileRef = storageRef(storage, filePath)

      const metadata: UploadMetadata = {
        contentType: file.type,
        ...options.metadata
      }

      const snapshot = await uploadBytes(fileRef, file, metadata)
      const url = await getDownloadURL(snapshot.ref)
      downloadURL.value = url
      return url
    } catch (err: any) {
      error.value = err as StorageError
      console.error('파일 업로드 중 오류 발생:', err)
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * Base64 문자열로부터 이미지 업로드
   */
  const uploadBase64Image = async (
    base64String: string,
    options: UploadOptions
  ): Promise<string | null> => {
    if (!base64String) return null

    loading.value = true
    error.value = null
    progress.value = 0
    downloadURL.value = null // downloadURL 상태 초기화 추가

    try {
      let filePath = options.path
      // Base64 업로드 시에도 generateUniqueName 처리
      if (options.generateUniqueName) {
        const uniqueId = Date.now().toString()
        // ContentType에서 확장자 추출 시도, 실패 시 기본값 사용
        const dataFormat = base64String.split(',')[0];
        const contentType = dataFormat.match(/:(.*?);/)?.[1] || 'image/jpeg';
        const extension = contentType.split('/')[1] || 'jpg'; // MIME 타입에서 확장자 추출
        filePath = `${options.path}/${uniqueId}.${extension}`
      }

      const dataFormat = base64String.split(',')[0]
      const contentType = dataFormat.match(/:(.*?);/)?.[1] || 'image/jpeg'
      const base64Data = base64String.includes('base64,') ? base64String.split('base64,')[1] : base64String

      const imageRef = storageRef(storage, filePath)

      const metadata: UploadMetadata = {
        contentType,
        ...options.metadata
      }

      const snapshot = await uploadString(imageRef, base64Data, 'base64', metadata)
      const url = await getDownloadURL(snapshot.ref)
      downloadURL.value = url
      return url
    } catch (err: any) {
      error.value = err as StorageError
      console.error('Base64 이미지 업로드 중 오류 발생:', err)
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * 프로필 이미지 업로드를 위한 편의 함수
   */
  const uploadProfileImage = async (
    file: File,
    userId: string,
    // generateUniqueName 기본값을 true로 변경하거나 필요에 따라 조절
    generateUniqueName = true
  ): Promise<string | null> => {
    const path = `profile-images/${userId}`; // 디렉토리 경로만 전달
    return uploadFile(file, {
      // generateUniqueName: true 로 설정하여 파일명이 자동으로 생성되도록 함
      path: path,
      generateUniqueName: generateUniqueName,
      metadata: {
        customMetadata: {
          'uploadedBy': userId,
          'purpose': 'profile-image'
        }
      }
    })
  }

  /**
   * 파일 삭제
   */
  const deleteFile = async (filePath: string): Promise<boolean> => {
    loading.value = true
    error.value = null

    try {
      const fileRef = storageRef(storage, filePath)
      await deleteObject(fileRef)
      return true
    } catch (err: any) {
      // 삭제하려는 파일이 없을 때 발생하는 에러(storage/object-not-found)는
      // 성공으로 간주할지 여부를 결정할 수 있습니다. 여기서는 false로 처리합니다.
      error.value = err as StorageError
      console.error('파일 삭제 중 오류 발생:', err)
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * 파일 URL 가져오기
   */
  const getFileURL = async (filePath: string): Promise<string | null> => {
    loading.value = true
    error.value = null
    downloadURL.value = null // URL 가져오기 전 초기화

    try {
      const fileRef = storageRef(storage, filePath)
      const url = await getDownloadURL(fileRef)
      downloadURL.value = url
      return url
    } catch (err: any) {
      // 파일을 찾을 수 없는 경우 등
      error.value = err as StorageError
      console.error('파일 URL 가져오기 실패:', err)
      downloadURL.value = null // 실패 시 null 유지
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * 파일 참조 가져오기
   */
  const getFileRef = (filePath: string): StorageReference => {
    return storageRef(storage, filePath)
  }

  // 반환 값에 listFiles 및 관련 상태 추가
  return {
    loading, // 개별 작업 로딩
    progress,
    error, // 개별 작업 에러
    downloadURL,
    listFiles,
    listLoading, // 목록 로딩 상태
    listError,   // 목록 에러 상태
    files,       // 파일 목록 (ref)
    uploadFile,
    uploadBase64Image,
    uploadProfileImage,
    deleteFile,
    getFileURL,
    getFileRef
  }
}